import { Icons } from '@dbrain/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { needsAcknowledgement, placeLabel, targetId, warningText, type SourceWording, type Target, type TargetList } from './model.js';

/**
 * "Nereye eklensin?" (MsBelgeSec) and "Nereye kaydedilsin?" (MsMailEkKaydet): one radio per place, Kişisel first and
 * chosen by default. A team, a workspace or Herkes shows the amber warning in the source's words with its "Anladım"
 * box inside the chosen card; the caller keeps its primary button disabled until the box is ticked. Herkes is a
 * locked line for everyone but a super admin. A change of place clears "Anladım" (it was said for the other place).
 */
export function TargetChooser({
  targets,
  value,
  onChange,
  acknowledged,
  onAcknowledge,
  wording,
  save = false,
  legendId,
  testId,
}: {
  targets: TargetList;
  /** the chosen place's id (targetId) */
  value: string;
  onChange: (id: string) => void;
  acknowledged: boolean;
  onAcknowledge: (on: boolean) => void;
  wording: SourceWording;
  /** the attachment card's popover: its own legend and the team's longer hint */
  save?: boolean;
  /** the legend names the popover around it */
  legendId?: string;
  testId: string;
}) {
  const { t } = useTranslation();
  const name = useId();
  return (
    <fieldset className={save ? 'm365-targets m365-targets--save' : 'm365-targets'} data-testid={testId}>
      <legend className="m365-targets__legend" id={legendId}>
        {t(save ? 'microsoft365.files.targets.legendSave' : 'microsoft365.files.targets.legend')}
      </legend>
      {targets.options.map((target) => (
        <TargetOption
          key={targetId(target)}
          target={target}
          name={name}
          selected={targetId(target) === value}
          onSelect={() => onChange(targetId(target))}
          acknowledged={acknowledged}
          onAcknowledge={onAcknowledge}
          warning={warningText(target, wording, t)}
          save={save}
        />
      ))}
      {targets.organizationLocked ? (
        <p className="m365-targets__locked" data-testid={`${testId}-locked`}>
          <Icons.lock size={15} />
          {t('microsoft365.files.targets.locked')}
        </p>
      ) : null}
    </fieldset>
  );
}

function hintOf(target: Target, save: boolean, t: (key: string) => string): string {
  switch (target.kind) {
    case 'user':
      return t('microsoft365.files.targets.userHint');
    case 'team':
      return t(save ? 'microsoft365.files.targets.teamHintSave' : 'microsoft365.files.targets.teamHint');
    case 'workspace':
      return t('microsoft365.files.targets.workspaceHint');
    case 'organization':
      return t('microsoft365.files.targets.organizationHint');
    case 'conversation':
      return t('microsoft365.files.targets.conversationHint');
  }
}

function TargetOption({
  target,
  name,
  selected,
  onSelect,
  acknowledged,
  onAcknowledge,
  warning,
  save,
}: {
  target: Target;
  name: string;
  selected: boolean;
  onSelect: () => void;
  acknowledged: boolean;
  onAcknowledge: (on: boolean) => void;
  warning: string | null;
  save: boolean;
}) {
  const { t } = useTranslation();
  const warningId = useId();
  const shown = selected && needsAcknowledgement(target) && warning !== null;
  return (
    <div className={selected ? 'm365-target m365-target--selected' : 'm365-target'} data-testid="m365-target" data-kind={target.kind} data-selected={selected ? 'true' : undefined}>
      <label className="m365-target__choice">
        <input type="radio" className="ui-radio" name={name} checked={selected} onChange={onSelect} aria-describedby={shown ? warningId : undefined} data-testid={`m365-target-${target.kind}`} />
        <span className="m365-target__text">
          <span className="m365-target__title">{placeLabel(target, t)}</span>
          <span className="m365-target__hint">{hintOf(target, save, t)}</span>
        </span>
      </label>
      {shown ? (
        <div className="m365-target__warning" data-testid="m365-target-warning">
          <Icons.warning size={16} />
          <span className="m365-target__warning-body">
            <span id={warningId}>{warning}</span>
            <label className="m365-target__ack">
              <input type="checkbox" checked={acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} data-testid="m365-target-ack" />
              <span>{t('microsoft365.files.targets.acknowledge')}</span>
            </label>
          </span>
        </div>
      ) : null}
    </div>
  );
}
